import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Typography } from '@material-ui/core';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Popper from '@material-ui/core/Popper';
import { makeStyles } from '@material-ui/core/styles';
import ArchiveOutlined from '@material-ui/icons/ArchiveOutlined';
import EditOutlined from '@material-ui/icons/EditOutlined';
import UnarchiveOutlined from '@material-ui/icons/UnarchiveOutlined';
import { useApi } from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { bugsyApiRef, type BugsyThread } from '../../api/BugsyApi';
import { bugsyAccessPermission } from '../../permissions';
import { bugsyRootPath, bugsyThreadPath } from '../../paths';

const DAY_MS = 86_400_000;

/**
 * The panel is a short inline disclosure under the nav item, not a page -- a
 * long scroll works against the sidebar instead of with it. Ten covers a
 * typical week of conversations; anything older is a click away in the
 * "See past chats" popup, which scrolls inside itself instead of the sidebar.
 */
const THREAD_LIMIT = 10;

// Text sits on the sidebar's navigation strip, which is always the dark VW
// blue regardless of the app's light/dark mode (see unifiedTheme.ts) -- so
// it uses the MUI navigation palette rather than the --modern-text-* tokens,
// which are calibrated for the page content area and would be unreadable
// here in light mode. Backgrounds and borders use the --bugsy-* tokens per
// the brief; they read subtly against the dark strip in both modes, which is
// the same "veil, not a panel" intent the chat page itself uses.
//
// Type comes from the app's own tokens rather than raw px, so the rail reads
// as part of the sidebar instead of a panel that brought its own font: the
// rows had been a hair smaller and lighter than every other nav label, which
// is exactly the kind of near-miss that looks like a different typeface.
//
// GUTTER + PADDING is one left inset the group labels and the rows share.
// The rows used to sit 12px left of it, pressed against the sidebar's own
// border. "+ New chat" is deliberately outside that alignment: it is the one
// action here rather than another item in the list, so it is inset further
// from both sidebar edges and centres its own label.
const ROW_GUTTER = 2; // 16px from the sidebar edge to a row's box
const ROW_PADDING = 1.5; // 12px more from the box to its text
const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1, 0, 2),
  },
  newChat: {
    display: 'block',
    margin: theme.spacing(0.5, 3, 1.5),
    padding: theme.spacing(1.25, ROW_PADDING),
    borderRadius: 8,
    // Thicker and on the stronger token: at 1px of --bugsy-border this edge
    // was under a tenth of an alpha against the navigation strip, which is
    // not a button outline so much as a rumour of one.
    border: '2px solid var(--bugsy-border-strong, transparent)',
    color: theme.palette.navigation?.color ?? '#FFFFFF',
    textAlign: 'center' as const,
    textDecoration: 'none',
    fontFamily: 'var(--modern-font-family, inherit)',
    fontWeight: 600,
    fontSize: 'var(--modern-font-size-base, 14px)',
  },
  group: {
    margin: theme.spacing(2, ROW_GUTTER + ROW_PADDING, 0.5),
    fontFamily: 'var(--modern-font-family, inherit)',
    fontSize: 'var(--modern-font-size-xs, 11px)',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.palette.navigation?.color ?? '#FFFFFF',
    opacity: 0.7,
  },
  rowWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    margin: theme.spacing(0, ROW_GUTTER),
    paddingRight: theme.spacing(1),
    borderRadius: 8,
    '&:hover': {
      background: 'var(--bugsy-surface-raised, transparent)',
    },
  },
  row: {
    flex: 1,
    minWidth: 0,
    display: 'block',
    padding: theme.spacing(1, ROW_PADDING),
    color: theme.palette.navigation?.color ?? '#FFFFFF',
    textDecoration: 'none',
    fontFamily: 'var(--modern-font-family, inherit)',
    fontSize: 'var(--modern-font-size-base, 14px)',
    fontWeight: 500,
    lineHeight: 'var(--modern-line-height-normal, 1.5)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Dimmed rather than hidden. Revealing these only on hover meant a reader
  // looking for a way to rename or archive a conversation found nothing to
  // look for -- the first thing they tried was double-clicking the row.
  rowAction: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    border: 'none',
    background: 'transparent',
    padding: 2,
    cursor: 'pointer',
    opacity: 0.6,
    color: theme.palette.navigation?.color ?? '#FFFFFF',
    '&:hover, &:focus': {
      opacity: 1,
    },
  },
  rowActionIcon: {
    fontSize: 15,
  },
  archivedToggle: {
    alignSelf: 'flex-start',
    margin: theme.spacing(2, ROW_GUTTER + ROW_PADDING, 0.5),
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left' as const,
    fontSize: 'var(--modern-font-size-xs, 11px)',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    fontFamily: 'var(--modern-font-family, inherit)',
    color: theme.palette.navigation?.color ?? '#FFFFFF',
    opacity: 0.7,
    '&:hover': {
      opacity: 1,
    },
  },
  archivedNote: {
    margin: theme.spacing(0, ROW_GUTTER + ROW_PADDING),
    fontFamily: 'var(--modern-font-family, inherit)',
    fontSize: 'var(--modern-font-size-sm, 12px)',
    color: theme.palette.navigation?.color ?? '#FFFFFF',
    opacity: 0.6,
  },
  // An archived thread is unreachable until it is restored -- `get_thread`
  // refuses it, so its own URL answers "could not be found". It is therefore
  // a name and a restore button, not a link to a dead end.
  archivedTitle: {
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(1, ROW_PADDING),
    color: theme.palette.navigation?.color ?? '#FFFFFF',
    opacity: 0.7,
    fontFamily: 'var(--modern-font-family, inherit)',
    fontSize: 'var(--modern-font-size-base, 14px)',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Sits with the rail's other text-button ("Archived"), but in sentence case
  // and at row size: it opens something, it does not label a group.
  pastChatsToggle: {
    alignSelf: 'flex-start',
    margin: theme.spacing(1, ROW_GUTTER + ROW_PADDING, 0),
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left' as const,
    fontFamily: 'var(--modern-font-family, inherit)',
    fontSize: 'var(--modern-font-size-sm, 12px)',
    fontWeight: 600,
    color: theme.palette.navigation?.color ?? '#FFFFFF',
    opacity: 0.8,
    textDecoration: 'underline',
    '&:hover, &:focus': {
      opacity: 1,
    },
  },
  // The popup opens beside the sidebar, over the page content, so unlike the
  // rail it is drawn with the page's tokens: the Bugsy page's own background
  // (--bui-bg-neutral-1), so it reads as a piece of the site rather than the
  // lighter --modern-bg-paper card, which is VW BlueMix in dark mode.
  pastChatsPopper: {
    zIndex: theme.zIndex.modal,
  },
  pastChatsPanel: {
    width: 320,
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 420,
    overflowY: 'auto',
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
    borderRadius: 10,
    border: '1px solid var(--modern-border-default, #dfe4e8)',
    background: 'var(--bui-bg-neutral-1, #fff)',
    boxShadow: 'var(--modern-shadow-3, 0 8px 20px rgba(0,0,0,0.12))',
  },
  pastChatsGroup: {
    padding: theme.spacing(1, 1.5),
    '& + &': {
      borderTop: '1px solid var(--modern-border-light, #f2f4f7)',
    },
  },
  pastChatsGroupName: {
    margin: theme.spacing(0, 0, 0.5),
    fontFamily: 'var(--modern-font-family, inherit)',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: 'uppercase' as const,
    color: 'var(--modern-text-secondary, #6a7a82)',
  },
  pastChatsList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  pastChatsItem: {
    display: 'block',
    padding: theme.spacing(0.5, 1),
    borderRadius: 6,
    fontFamily: 'var(--modern-font-family, inherit)',
    fontSize: 13,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: 'var(--modern-text-primary, #001e50)',
    textDecoration: 'none',
    '&:hover, &:focus': {
      background: 'var(--bugsy-surface-raised, #f2f4f7)',
    },
  },
  pastChatsItemCurrent: {
    color: 'var(--modern-accent, #128836)',
    fontWeight: 600,
  },
  renameInput: {
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(1, ROW_PADDING),
    borderRadius: 8,
    border: '1px solid var(--bugsy-border, transparent)',
    background: 'var(--bugsy-surface-raised, transparent)',
    color: theme.palette.navigation?.color ?? '#FFFFFF',
    fontFamily: 'var(--modern-font-family, inherit)',
    fontSize: 'var(--modern-font-size-base, 14px)',
    fontWeight: 500,
  },
}));

/** Today / Yesterday / Earlier, by last activity. Ported from the page rail this replaces. */
function bucketOf(thread: BugsyThread, now: number): string {
  const updated = thread.updated_at ? Date.parse(thread.updated_at) : 0;
  if (!updated) return 'Earlier';
  const age = now - updated;
  if (age < DAY_MS) return 'Today';
  if (age < 2 * DAY_MS) return 'Yesterday';
  return 'Earlier';
}

/**
 * The past-chats popup's finer buckets: the whole history is in it, so
 * "Earlier" would be most of the list. Past a month, a calendar month is
 * the unit people remember a conversation by.
 */
export function historyBucketOf(thread: BugsyThread, now: number): string {
  const updated = thread.updated_at ? Date.parse(thread.updated_at) : 0;
  if (!updated) return 'Older';
  const age = now - updated;
  if (age < DAY_MS) return 'Today';
  if (age < 2 * DAY_MS) return 'Yesterday';
  if (age < 7 * DAY_MS) return 'Previous 7 days';
  if (age < 30 * DAY_MS) return 'Previous 30 days';
  return new Date(updated).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

const byRecency = (a: BugsyThread, b: BugsyThread) =>
  Date.parse(b.updated_at ?? '') - Date.parse(a.updated_at ?? '');

/**
 * The "+ New chat" and thread list content of the Bugsy AI sidebar's inline,
 * click-to-expand disclosure.
 *
 * Lives in the plugin, not the app, because the data fetching needs
 * `bugsyApiRef` and `bugsyAccessPermission` -- both plugin-private.
 * `Sidebar.tsx` only mounts this component once the "Bugsy AI" item has been
 * clicked open -- it is never mounted, and so never fetches, on a page load
 * that leaves the section collapsed. That mount-gating is the fetch guard;
 * this component still fetches unconditionally on its own mount, same as
 * before.
 */
export const BugsyThreadsPanel = () => {
  const classes = useStyles();
  const bugsyApi = useApi(bugsyApiRef);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { loading, allowed } = usePermission({ permission: bugsyAccessPermission });
  // Every live thread, newest first. The rail shows the head of it; the
  // past-chats popup shows all of it.
  const [threads, setThreads] = useState<BugsyThread[]>([]);
  const [pastChatsOpen, setPastChatsOpen] = useState(false);
  const pastChatsAnchor = useRef<HTMLButtonElement>(null);
  // The thread whose title is currently being edited in place, and the text
  // typed so far. Only ever one row at a time.
  const [renamingId, setRenamingId] = useState<string | undefined>();
  const [renameValue, setRenameValue] = useState('');
  // The archive, fetched only when the reader opens it: it is the exception,
  // not something to pay for on every expand of the panel.
  const [showArchived, setShowArchived] = useState(false);
  const [archived, setArchived] = useState<BugsyThread[]>([]);
  const [archivedFailed, setArchivedFailed] = useState(false);
  // The input replaces the row only because the reader asked to rename it, so
  // it takes focus the moment it appears, with the old title selected to type
  // over. A ref rather than `autoFocus`, which is banned for moving focus on
  // load -- and a stable one, or it would re-fire and re-select on every
  // keystroke.
  const focusRename = useCallback((input: HTMLInputElement | null) => {
    input?.focus();
    input?.select();
  }, []);
  // Guards against re-fetching (and re-setState-ing) if this effect re-fires
  // on a render where `bugsyApi`'s identity changed but nothing meaningful
  // did -- `useApi` is not obliged to return the same object twice, and a
  // test that mocks it as a fresh literal per call has, twice before on this
  // branch, turned an unconditional fetch-then-setState into a loop that
  // never lets Jest finish. One mount is one expand of the panel, so "once
  // per mount" is already the right frequency.
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (loading || !allowed || hasFetchedRef.current) {
      return undefined;
    }
    hasFetchedRef.current = true;
    let cancelled = false;
    bugsyApi
      .listThreads()
      .then(list => {
        if (cancelled) return;
        setThreads([...list].sort(byRecency));
      })
      // History is secondary to asking a question; a failure here leaves the
      // panel with just "+ New chat" rather than an error in a sidebar
      // section no one can act on.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [loading, allowed, bugsyApi]);

  // Both actions write to the server first and only then to the list on
  // screen: a rename or an archive the gateway rejected must leave the rail
  // showing what the server still holds, not what the reader attempted.
  const commitRename = async (thread: BugsyThread) => {
    const title = renameValue.trim();
    setRenamingId(undefined);
    if (!title || title === thread.title) {
      return;
    }
    try {
      await bugsyApi.renameThread(thread.thread_id, title);
      setThreads(prev =>
        prev.map(row => (row.thread_id === thread.thread_id ? { ...row, title } : row)),
      );
    } catch {
      // Left as it was. The rail has no room for an error, and the title the
      // reader can still see is the one the server actually has.
    }
  };

  const archive = async (thread: BugsyThread) => {
    try {
      await bugsyApi.archiveThread(thread.thread_id);
    } catch {
      return;
    }
    setThreads(prev => prev.filter(row => row.thread_id !== thread.thread_id));
    setArchived(prev => [thread, ...prev]);
    // An archived thread is refused by `get_thread`, so the conversation the
    // reader is looking at has just become unreachable -- including by the URL
    // they are on. Move them somewhere that still exists.
    if (pathname === bugsyThreadPath(thread.thread_id)) {
      navigate(bugsyRootPath);
    }
  };

  const unarchive = async (thread: BugsyThread) => {
    try {
      await bugsyApi.unarchiveThread(thread.thread_id);
    } catch {
      return;
    }
    setArchived(prev => prev.filter(row => row.thread_id !== thread.thread_id));
    setThreads(prev => [thread, ...prev].sort(byRecency));
  };

  // Refetched on each open rather than cached: what is in the archive changes
  // from this very panel, and a list that is one archive behind is worse than
  // the fetch it saves.
  const toggleArchived = async () => {
    if (showArchived) {
      setShowArchived(false);
      return;
    }
    setShowArchived(true);
    setArchivedFailed(false);
    try {
      setArchived(await bugsyApi.listThreads({ archived: true }));
    } catch {
      setArchived([]);
      setArchivedFailed(true);
    }
  };

  // Escape closes the popup like the sources panel, and hands focus back to
  // the button that opened it.
  useEffect(() => {
    if (!pastChatsOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPastChatsOpen(false);
        pastChatsAnchor.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [pastChatsOpen]);

  // No access: fail to an empty panel, never an error and never a request
  // a reader without bugsy.access has any use for.
  if (loading || !allowed) {
    return null;
  }

  const now = Date.now();
  const groups = ['Today', 'Yesterday', 'Earlier'] as const;
  const recent = threads.slice(0, THREAD_LIMIT);
  // Buckets in the order they first appear; `threads` is newest first, so
  // that is already newest bucket first.
  const history = new Map<string, BugsyThread[]>();
  for (const thread of threads) {
    const bucket = historyBucketOf(thread, now);
    history.set(bucket, [...(history.get(bucket) ?? []), thread]);
  }

  return (
    <div className={classes.root}>
      <Link className={classes.newChat} to={bugsyRootPath}>
        + New chat
      </Link>
      {groups.map(group => {
        const inGroup = recent.filter(thread => bucketOf(thread, now) === group);
        if (!inGroup.length) return null;
        return (
          <div key={group}>
            <Typography className={classes.group}>{group}</Typography>
            {inGroup.map(thread => (
              <div key={thread.thread_id} className={classes.rowWrap}>
                {renamingId === thread.thread_id ? (
                  <input
                    className={classes.renameInput}
                    aria-label="Conversation title"
                    value={renameValue}
                    ref={focusRename}
                    onChange={event => setRenameValue(event.target.value)}
                    onKeyDown={event => {
                      if (event.key === 'Enter') commitRename(thread);
                      if (event.key === 'Escape') setRenamingId(undefined);
                    }}
                    // Clicking away abandons the edit rather than committing
                    // it: a rename nobody confirmed is not one they asked for.
                    onBlur={() => setRenamingId(undefined)}
                  />
                ) : (
                  <>
                    <Link
                      className={classes.row}
                      to={bugsyThreadPath(thread.thread_id)}
                      title={thread.title}
                    >
                      {thread.title}
                    </Link>
                    <button
                      className={classes.rowAction}
                      aria-label={`Rename ${thread.title}`}
                      onClick={() => {
                        setRenameValue(thread.title);
                        setRenamingId(thread.thread_id);
                      }}
                    >
                      <EditOutlined className={classes.rowActionIcon} />
                    </button>
                    <button
                      className={classes.rowAction}
                      aria-label={`Archive ${thread.title}`}
                      onClick={() => archive(thread)}
                    >
                      <ArchiveOutlined className={classes.rowActionIcon} />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        );
      })}
      {threads.length > THREAD_LIMIT && (
        <ClickAwayListener onClickAway={() => pastChatsOpen && setPastChatsOpen(false)}>
          <div>
            <button
              ref={pastChatsAnchor}
              type="button"
              className={classes.pastChatsToggle}
              aria-expanded={pastChatsOpen}
              aria-controls="bugsy-past-chats"
              onClick={() => setPastChatsOpen(open => !open)}
            >
              See past chats
            </button>
            {/* Portalled: the sidebar clips its own overflow, and the popup
                has to stand out beside it, over the page. */}
            <Popper
              open={pastChatsOpen}
              anchorEl={pastChatsAnchor.current}
              placement="right-start"
              className={classes.pastChatsPopper}
              modifiers={{ flip: { enabled: true }, preventOverflow: { boundariesElement: 'viewport' } }}
            >
              <div
                id="bugsy-past-chats"
                role="dialog"
                aria-label="Past chats"
                className={classes.pastChatsPanel}
              >
                {[...history].map(([bucket, inBucket]) => (
                  <section key={bucket} className={classes.pastChatsGroup} aria-label={bucket}>
                    <Typography className={classes.pastChatsGroupName}>{bucket}</Typography>
                    <ul className={classes.pastChatsList}>
                      {inBucket.map(thread => {
                        const path = bugsyThreadPath(thread.thread_id);
                        return (
                          <li key={thread.thread_id}>
                            <Link
                              className={`${classes.pastChatsItem} ${
                                pathname === path ? classes.pastChatsItemCurrent : ''
                              }`}
                              to={path}
                              title={thread.title}
                              aria-current={pathname === path ? 'page' : undefined}
                              onClick={() => setPastChatsOpen(false)}
                            >
                              {thread.title}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                ))}
              </div>
            </Popper>
          </div>
        </ClickAwayListener>
      )}
      <button className={classes.archivedToggle} onClick={toggleArchived}>
        {showArchived ? 'Hide archived' : 'Archived'}
      </button>
      {showArchived && archivedFailed && (
        <Typography className={classes.archivedNote}>
          Could not load the archive
        </Typography>
      )}
      {showArchived && !archivedFailed && !archived.length && (
        <Typography className={classes.archivedNote}>Nothing archived</Typography>
      )}
      {showArchived &&
        archived.map(thread => (
          <div key={thread.thread_id} className={classes.rowWrap}>
            <span className={classes.archivedTitle} title={thread.title}>
              {thread.title}
            </span>
            <button
              className={classes.rowAction}
              aria-label={`Restore ${thread.title}`}
              onClick={() => unarchive(thread)}
            >
              <UnarchiveOutlined className={classes.rowActionIcon} />
            </button>
          </div>
        ))}
    </div>
  );
};
